import React, {useState, useEffect} from 'react';
import {DatePicker, Button} from 'antd';
import {CalendarOutlined} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useWarehouseContext } from "../../../../contexts/WarehouseContext";
import { getTodayInTimezone, getCurrentTimeInTimezone } from '../../../../utils/timezoneUtils';

// 扩展 dayjs 支持时区功能
dayjs.extend(utc);
dayjs.extend(timezone);

interface MultiDateSelectorProps {
    value?: string[];
    onChange?: (dates: string[]) => void;
    minimumDate?: Dayjs;
    disabled?: boolean;
}

const MultiDateSelector: React.FC<MultiDateSelectorProps> = ({
    value = [], 
    onChange, 
    minimumDate, 
    disabled = false
}) => {
    const { currentWarehouse: warehouse } = useWarehouseContext();

    // 计算实际的最小日期，基于 warehouse 时区的当天
    const getActualMinimumDate = () => {
        if (minimumDate) {
            return minimumDate;
        }
        
        // 如果有 warehouse 时区，根据截止时间判断最小日期
        if (warehouse?.timezone && warehouse?.appointmentDeadlineTime) {
            const currentTimeInWarehouseZone = getCurrentTimeInTimezone(warehouse.timezone);
            
            // Parse deadline time (format: "HH:mm")
            const [hours, minutes] = warehouse.appointmentDeadlineTime.split(':').map(Number);
            const todayDeadlineInWarehouseZone = currentTimeInWarehouseZone
                .hour(hours)
                .minute(minutes)
                .second(0)
                .millisecond(0);

            // 如果当前时间超过了今天的截止时间，最小日期是明天
            if (currentTimeInWarehouseZone.isAfter(todayDeadlineInWarehouseZone)) {
                return currentTimeInWarehouseZone.add(1, 'day').startOf('day');
            } else {
                return currentTimeInWarehouseZone.startOf('day');
            }
        } else if (warehouse?.timezone) {
            // 没有截止时间设置，使用该时区的当天
            return getTodayInTimezone(warehouse.timezone);
        }
        
        // 否则使用本地时区的当天
        return dayjs().startOf('day');
    };

    const actualMinimumDate = getActualMinimumDate();
    const [selectedDates, setSelectedDates] = useState<Dayjs[]>(
        value?.length ? value.map(date => dayjs(date)) : [actualMinimumDate]
    );

    // 同步外部value和内部状态
    useEffect(() => {
        if (Array.isArray(value) && value.length > 0) {
            setSelectedDates(value.map(date => dayjs(date)));
        } else if (!value || value.length === 0) {
            // 确保至少有默认日期
            setSelectedDates([actualMinimumDate]);
        }
    }, [value, actualMinimumDate]);

    // 处理日期变化
    const handleDateChange = (dates: Dayjs[] | null) => {
        if (dates && dates.length > 0) {
            // 过滤掉小于最小日期的日期
            const validDates = dates.filter(date => 
                date.isAfter(actualMinimumDate.subtract(1, 'day'))
            );
            
            if (validDates.length > 0) {
                setSelectedDates(validDates);
                // 将Dayjs对象转换为字符串数组
                const dateStrings = validDates.map(date => date.format('YYYY-MM-DD'));
                onChange?.(dateStrings);
            } else {
                // 如果没有有效日期，默认选中最小日期
                setSelectedDates([actualMinimumDate]);
                onChange?.([actualMinimumDate.format('YYYY-MM-DD')]);
            }
        } else {
            // 如果清空了所有日期，默认选中最小日期
            setSelectedDates([actualMinimumDate]);
            onChange?.([actualMinimumDate.format('YYYY-MM-DD')]);
        }
    };

    return (
        <div className="multi-date-selector w-full">
            <div className={`border border-gray-300 rounded flex justify-between items-center py-1 px-2 ${disabled ? 'bg-gray-50 cursor-not-allowed' : ''}`}>
                <div className={`pl-2 ${disabled ? 'text-gray-400' : 'text-gray-700'}`}>
                    ({selectedDates.length}days)
                </div>
                <DatePicker
                    multiple
                    value={selectedDates}
                    onChange={handleDateChange}
                    disabled={disabled}
                    disabledDate={(current) => {
                        // 禁用最小日期之前的日期
                        return current && current < actualMinimumDate.startOf('day');
                    }}
                    placeholder="option date"
                    allowClear={false}
                    suffixIcon={null}
                    variant="borderless"
                    className="w-0 h-0 opacity-0 absolute"
                />
                <Button 
                    type="text" 
                    icon={<CalendarOutlined />} 
                    disabled={disabled}
                    onClick={() => {
                        if (!disabled) {
                            // 找到并点击隐藏的DatePicker
                            const datePickerInput = document.querySelector('.multi-date-selector .ant-picker-input input');
                            if (datePickerInput) {
                                (datePickerInput as HTMLElement).click();
                            }
                        }
                    }}
                />
            </div>
        </div>
    );
};

export default MultiDateSelector;
